import { AnyAri } from '@atlassian/ari/any-ari';
import type { AsapClaims } from '@atlassian/asap-interface';
import jwtDecode from 'jwt-decode';

import { Deferrable } from '@canvas/deferrable';
import { type Action, isAction } from '@canvas/model/permissions';
import { captureException } from '@canvas/sentry-core';

import { CancellableOperation } from './cancellable-operation.ts';
import { TOKEN_EXPIRY, TOKEN_FETCH_TIMEOUT_MS } from './constants.ts';
import {
  TokenIdMismatchError,
  TokenFetchError,
  type Token,
  type TokenRefresher,
  type TokenRefresherError,
  type TokenClaimsSynchronizer,
  type TokenClaimsSynchronizerError,
  type PaywallFeaturesSynchronizer,
  type TokenManager,
} from './types.ts';
import { resetClaims, abortSignalWithTimeout, withAbortSignal } from './utils.ts';

export class TokenManagerV1 implements TokenManager {
  private deferrable: Deferrable<Token>;
  private lastFetchedTime: number | null = null;
  private refresher: TokenRefresher | undefined;
  private refresherError: TokenRefresherError | undefined;
  private synchronizer: TokenClaimsSynchronizer | undefined;
  private synchronizerError: TokenClaimsSynchronizerError | undefined;
  private paywallFeaturesSynchronizer: PaywallFeaturesSynchronizer | undefined;
  private pendingTokenFetch: CancellableOperation<Token | undefined> | undefined;

  constructor() {
    this.deferrable = new Deferrable();
  }

  async token(retriesRemaining = 5): Promise<Token> {
    try {
      const token = await this.getTokenWithTimeout();

      if (!this.hasExpired(token)) {
        return token;
      }
    } catch (err) {
      console.error('Error getting token', err);
    }

    if (retriesRemaining > 1) {
      // If the token has expired, we need to refresh it (note that this doesn't throw)
      await this.refreshToken();

      return this.token(retriesRemaining - 1);
    }

    const error = new TokenFetchError('Could not fetch token after multiple attempts');

    captureException(error, {
      tags: {
        captureLocation: 'TokenManagerV1-token-finalFailure',
      },
    });

    throw error;
  }

  async tokenForDocumentIdOrThrow(boardIdOrAri: string): Promise<Token> {
    const token = await this.token();

    const documentId = AnyAri.parse(token.board).resourceId;
    if (documentId !== boardIdOrAri && boardIdOrAri !== token.board) {
      throw new TokenIdMismatchError();
    }

    return token;
  }

  updateToken(token: Token | undefined) {
    this.pendingTokenFetch?.cancel();

    if (this.isEmpty(token)) {
      this.deferrable = new Deferrable();

      this.lastFetchedTime = null;
    } else {
      if (this.deferrable.fulfilled) {
        this.deferrable = new Deferrable();
        this.deferrable.resolve(token);
      } else {
        this.deferrable.resolve(token);
      }

      this.lastFetchedTime = Date.now();
    }

    this.synchronizeAccessClaims(token);

    this.synchronizePaywallFeatures(token);
  }

  private parseClaims(token: Token): AsapClaims | undefined {
    const decodedToken: AsapClaims = jwtDecode(token.accessToken);
    if (!('access' in decodedToken)) {
      return;
    }

    return decodedToken.access;
  }

  private synchronizeAccessClaims = (token: Token | undefined) => {
    /**
     * If no one is listening for claims changes, ignore.
     */
    if (!this.synchronizer) {
      return;
    }

    const boardId =
      token !== undefined && token.board.length > 0 ? token.board : undefined;

    /**
     * If the token is empty, treat it as a permissions resetting event.
     */
    if (this.isEmpty(token)) {
      this.synchronizer(boardId, resetClaims());
      return;
    }

    try {
      const claims = this.parseClaims(token);
      /**
       * If no claims are provided, treat it as a permissions resetting event.
       */
      if (!claims) {
        this.synchronizer(boardId, resetClaims());
        return;
      }

      const maybeBoardClaims: any[] = claims[token.board] ?? [];

      const boardClaims = new Set<Action>(maybeBoardClaims.filter(isAction));

      this.synchronizer(boardId, { board: boardClaims });
    } catch (err) {
      /**
       * In the event of an error, fallback to an empty claim set.
       */
      this.synchronizer(boardId, resetClaims());
      this.synchronizerError?.(err);
    }
  };

  private synchronizePaywallFeatures = (token: Token | undefined) => {
    if (!this.paywallFeaturesSynchronizer) {
      return;
    }

    if (token === undefined) {
      return;
    }

    if (token.features === undefined) {
      return;
    }

    this.paywallFeaturesSynchronizer(token.features);
  };

  /**
   * Refreshes the token by fetching a new token through the provided refresher
   * @param initialFetch If the parameter is true, will try to fetch a new token
   * and resolve the existing deferrable with the newly fetched token
   */
  async refreshToken(initialFetch = false) {
    if (!this.refresher) {
      return;
    }

    const refresher = this.refresher;
    if (!initialFetch) {
      // Wait for new token
      this.deferrable = new Deferrable();
      this.lastFetchedTime = null;

      // Reset all claims
      this.synchronizer?.(undefined, resetClaims());
    }

    // Start fetching a new token
    try {
      this.pendingTokenFetch?.cancel();
      this.pendingTokenFetch = new CancellableOperation(
        () => refresher(),
        this.updateToken.bind(this),
      );

      await this.pendingTokenFetch.execute();
    } catch (err) {
      this.refresherError?.(err);
    }
  }

  /**
   * Initializes the first token even if there is no previous token in the manager
   */
  async initializeToken() {
    await this.refreshToken(true);
  }

  onRefreshToken(handler: () => Promise<Token | undefined>) {
    this.refresher = handler;
  }

  onRefreshTokenErrored(handler: TokenRefresherError) {
    this.refresherError = handler;
  }

  onSynchronizeClaims(handler: TokenClaimsSynchronizer) {
    this.synchronizer = handler;
  }

  onSynchronizeClaimsError(handler: TokenClaimsSynchronizerError) {
    this.synchronizerError = handler;
  }

  onPaywallFeatureFetch(handler: PaywallFeaturesSynchronizer) {
    this.paywallFeaturesSynchronizer = handler;
  }

  isEmpty(token: Token | undefined): token is undefined {
    return !(
      (token && token.accessToken && token.accessToken.length > 0)
      /**
       * TODO(Home): Bring the below back once we have killed Home.
       */
      // token.mediaToken &&
      // token.mediaToken.length > 0 &&
      // token.mediaBaseUrl &&
      // token.mediaBaseUrl.length > 0 &&
      // token.mediaCollectionId &&
      // token.mediaCollectionId.length > 0
    );
  }

  hasExpired(token: Token) {
    if (this.isEmpty(token) || this.lastFetchedTime === null) {
      return false;
    }

    // This check needs to rely on local time only. We can't use the expiry field of the
    // token, as we can't guarantee that the user's local time is in sync with the
    // backend.
    return Date.now() - this.lastFetchedTime > TOKEN_EXPIRY;
  }

  private getTokenWithTimeout() {
    const abortOperation = abortSignalWithTimeout(TOKEN_FETCH_TIMEOUT_MS);
    return withAbortSignal(
      this.deferrable.get(),
      abortOperation.signal,
      `Token fetch timed out after ${TOKEN_FETCH_TIMEOUT_MS}ms`,
    ).finally(abortOperation.clear);
  }
}
